import { React, useEffect, useState, useMemo } from "react";
import cogoToast from "cogo-toast";
import { SUCCESS_RESULT } from "../../Constants";
import { getUsers, getAuditLogsApi } from "../../services/Api";
import { Badge, Spinner, Table, Button } from "reactstrap";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FaSearch, FaEye } from "react-icons/fa";

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    start_date: formatInputDate(start),
    end_date: formatInputDate(end),
  };
};

const formatJsonBlock = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const eventBadgeColor = (event) => {
  const normalized = (event || "").toLowerCase();
  if (normalized === "created") return "success";
  if (normalized === "updated") return "primary";
  if (normalized === "deleted") return "danger";
  return "secondary";
};

const getActorDisplayName = (log) => {
  if (log?.user_name?.trim()) return log.user_name.trim();
  if (log?.user_email?.trim()) return log.user_email.trim();
  return "System";
};

const AuditLogs = () => {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total: 0,
    last_page: 1,
  });
  const [filters, setFilters] = useState({
    start_date: defaultRange.start_date,
    end_date: defaultRange.end_date,
    user_id: "",
    per_page: 50,
  });
  const [tableSearch, setTableSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadUsers();
    fetchAuditLogs(1);
    return () => {};
  }, []);

  const loadUsers = () => {
    setIsLoadingUsers(true);
    getUsers()
      .then((res) => {
        setIsLoadingUsers(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setUsers(res.data.data || []);
        }
      })
      .catch(() => {
        setIsLoadingUsers(false);
      });
  };

  const fetchAuditLogs = (page = 1) => {
    if (!filters.start_date || !filters.end_date) {
      cogoToast.error("Start date and end date are required");
      return;
    }
    if (filters.end_date < filters.start_date) {
      cogoToast.error("End date must be on or after start date");
      return;
    }

    const payload = {
      start_date: filters.start_date,
      end_date: filters.end_date,
      per_page: parseInt(filters.per_page, 10) || 50,
      page,
    };
    if (filters.user_id) {
      payload.user_id = parseInt(filters.user_id, 10);
    }

    setIsLoading(true);
    getAuditLogsApi(payload)
      .then((res) => {
        setIsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const data = res.data.data || {};
          setItems(data.items || []);
          setPagination({
            current_page: data.pagination?.current_page || page,
            per_page: data.pagination?.per_page || payload.per_page,
            total: data.pagination?.total || 0,
            last_page: data.pagination?.last_page || 1,
          });
        } else {
          cogoToast.error(res.data?.message || "Failed to load audit logs");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
        cogoToast.error("Something went wrong, please try again.");
      });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchAuditLogs(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.last_page || isLoading) return;
    fetchAuditLogs(newPage);
  };

  const openDetails = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const filteredItems = useMemo(() => {
    const term = tableSearch.trim().toLowerCase();
    if (!term) return items;
    return items.filter((log) => {
      const haystack = [
        log.user_name,
        log.user_email,
        log.event,
        log.auditable_model,
        log.auditable_id,
        log.ip_address,
        log.url,
        log.created_at,
        JSON.stringify(log.old_values || {}),
        JSON.stringify(log.new_values || {}),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [items, tableSearch]);

  return (
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Audit Logs</h1>
        {isLoading && <Spinner />}
      </div>

      <form onSubmit={handleSearch} className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label">Start date *</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={(e) => handleFilterChange("start_date", e.target.value)}
                required
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">End date *</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                required
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">User (optional)</label>
              <select
                className="form-control"
                value={filters.user_id}
                onChange={(e) => handleFilterChange("user_id", e.target.value)}
                disabled={isLoadingUsers}
              >
                <option value="">All users</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 mb-3">
              <label className="form-label">Per page</label>
              <select
                className="form-control"
                value={filters.per_page}
                onChange={(e) => handleFilterChange("per_page", e.target.value)}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            <div className="col-md-1 mb-3 d-flex align-items-end">
              <Button color="primary" type="submit" className="w-100" disabled={isLoading}>
                <FaSearch />
              </Button>
            </div>
          </div>
        </div>
      </form>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search loaded results by user, event, model, IP, URL..."
          value={tableSearch}
          onChange={(e) => setTableSearch(e.target.value)}
        />
      </div>

      <div className="table-responsive">
        <Table striped bordered hover className="mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>User</th>
              <th>Event</th>
              <th>Model</th>
              <th>Record ID</th>
              <th>IP Address</th>
              <th style={{ width: 90 }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center py-4">
                  <Spinner /> Loading audit logs...
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  {tableSearch.trim() ? "No logs match your search." : "No audit logs found for this range."}
                </td>
              </tr>
            ) : (
              filteredItems.map((log) => (
                <tr key={log.id}>
                  <td className="text-nowrap">{log.created_at || "—"}</td>
                  <td>
                    <div>{getActorDisplayName(log)}</div>
                    {log.user_email && (
                      <small className="text-muted">{log.user_email}</small>
                    )}
                  </td>
                  <td>
                    <Badge color={eventBadgeColor(log.event)} pill>
                      {log.event || "—"}
                    </Badge>
                  </td>
                  <td>{log.auditable_model || "—"}</td>
                  <td>
                    <code style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
                      {log.auditable_id || "—"}
                    </code>
                  </td>
                  <td>{log.ip_address || "—"}</td>
                  <td>
                    <Button color="info" size="sm" onClick={() => openDetails(log)}>
                      <FaEye />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {pagination.last_page > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Page {pagination.current_page} of {pagination.last_page} ({pagination.per_page} per page,{" "}
            {pagination.total.toLocaleString()} total)
          </small>
          <nav aria-label="Audit logs pagination">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${pagination.current_page === 1 || isLoading ? "disabled" : ""}`}>
                <button
                  type="button"
                  className="page-link"
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || isLoading}
                >
                  &laquo;
                </button>
              </li>
              <li className="page-item active">
                <span className="page-link">
                  {pagination.current_page} / {pagination.last_page}
                </span>
              </li>
              <li
                className={`page-item ${
                  pagination.current_page >= pagination.last_page || isLoading ? "disabled" : ""
                }`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page >= pagination.last_page || isLoading}
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      <Modal isOpen={detailsOpen} toggle={() => setDetailsOpen(false)} size="lg">
        <ModalHeader toggle={() => setDetailsOpen(false)}>Audit Log Details</ModalHeader>
        <ModalBody>
          {selectedLog && (
            <>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>User:</strong> {getActorDisplayName(selectedLog)}
                  {selectedLog.user_email ? ` (${selectedLog.user_email})` : ""}
                </div>
                <div className="col-md-6">
                  <strong>Date:</strong> {selectedLog.created_at || "—"}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-4">
                  <strong>Event:</strong> {selectedLog.event || "—"}
                </div>
                <div className="col-md-4">
                  <strong>Model:</strong> {selectedLog.auditable_model || "—"}
                </div>
                <div className="col-md-4">
                  <strong>Record ID:</strong> {selectedLog.auditable_id || "—"}
                </div>
              </div>
              <div className="mb-3">
                <strong>URL:</strong>
                <div className="text-break small">{selectedLog.url || "—"}</div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>IP Address:</strong> {selectedLog.ip_address || "—"}
                </div>
                <div className="col-md-6">
                  <strong>Tags:</strong> {selectedLog.tags || "—"}
                </div>
              </div>
              <div className="mb-3">
                <strong>User Agent:</strong>
                <div className="small text-muted">{selectedLog.user_agent || "—"}</div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <strong>Old values</strong>
                  <pre
                    className="bg-light border rounded p-2 mt-1 mb-0"
                    style={{ maxHeight: 220, overflow: "auto", fontSize: "0.8rem" }}
                  >
                    {formatJsonBlock(selectedLog.old_values)}
                  </pre>
                </div>
                <div className="col-md-6 mb-3">
                  <strong>New values</strong>
                  <pre
                    className="bg-light border rounded p-2 mt-1 mb-0"
                    style={{ maxHeight: 220, overflow: "auto", fontSize: "0.8rem" }}
                  >
                    {formatJsonBlock(selectedLog.new_values)}
                  </pre>
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AuditLogs;
